'use client'

import { useLanguage } from '@/components/Link'
import type { VendorSearchState } from '@/components/page-specific/catering/suppliers/states/useVendorSearchState'
import { SidebarSearchField } from '@/components/ui/TextField/SidebarSearchField'
import { Search } from '@mui/icons-material'
import { Stack, Typography } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'

import { enGB, nl, de, it } from 'date-fns/locale'
import debounce from 'lodash/debounce'
import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import type { UserBasketSettings } from '../../../common/domain/user-basket-settings'

type Props = {
  userBasketSettings: UserBasketSettings

  called: boolean
  search: VendorSearchState['search']
  filterCount: VendorSearchState['filterCount']
  toggleFilterOpen: () => void
  filters: VendorSearchState['filters']
  placeholder?: string
  helperText?: string
}

const SEARCH_DEBOUNCE_MS = 300

const SupplierSearch: React.FC<Props> = ({ called, search, filters, placeholder, helperText }) => {
  const { t } = useTranslation()
  const language = useLanguage()
  const datePickerLang =
    language === 'nl' ? nl : language === 'de' ? de : language === 'it' ? it : enGB

  const [localSearch, setLocalSearch] = useState(filters.search || '')

  useEffect(() => {
    setLocalSearch(filters.search || '')
  }, [filters.search])

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        search({ search: value })
      }, SEARCH_DEBOUNCE_MS),
    [search],
  )

  useEffect(() => {
    return () => {
      debouncedSearch.cancel()
    }
  }, [debouncedSearch])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setLocalSearch(value)
    debouncedSearch(value)
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={datePickerLang}>
      {called ? (
        <Stack spacing={1} direction="column">
          <SidebarSearchField
            id="menu-item-search"
            placeholder={placeholder ?? t('marketplace:searchVendors', 'Search Vendor Name')}
            startIcon={<Search />}
            onChange={handleSearchChange}
            value={localSearch}
          />
          {helperText && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
              {helperText}
            </Typography>
          )}
        </Stack>
      ) : null}
    </LocalizationProvider>
  )
}

export default SupplierSearch
export { SupplierSearch }
